<template>
  <div>
    <h5>Sales Report</h5>

    <b-card class="mb-4">
      <b-form inline @submit.prevent="submit">
        <ReportPeriodFilter
          :period-type.sync="periodType"
          :time-period.sync="timePeriod"
          :selected-dates.sync="selectedDates"
          :time-options="timeOptions"
          :date-config="fltConfig"
        />

        <b-select class="custom-select ml-5" v-model="filterType" :options="dataOptions" />

        <button class="btn btn-primary ml-3" :disabled="loading" @click.stop="excelExport = false">Show Report</button>
        <button class="btn btn-secondary ml-3" :disabled="loading" @click.stop="excelExport = true">Export XLSX</button>
      </b-form>
    </b-card>

    <LoadingIndicator v-if="loading" centered />

    <span v-if="empty">No records found</span>

    <b-card class="mb-4" v-if="!loading && items.length">
      <h6 class="mb-3">Retail Sales <small class="text-muted">(all sources except NDP)</small></h6>
      <b-table striped hover :items="items" :fields="fields" caption-top>
        <template #table-caption>
          <span v-if="form.dispatchedStart">Dispatched from {{ form.dispatchedStart }} till {{ form.dispatchedEnd }}</span>
          <span v-if="form.createdStart">Created from {{ form.createdStart }} till {{ form.createdEnd }}</span>
        </template>
        <template #cell(userId)="data">
          <b-link v-if="data.value" :to="userLink(data.value)" target="_blank">{{ data.item.user ? data.item.user.name : '' }}</b-link>
        </template>

        <template #cell(quotesCount)="row">
          <b-link :to="{ name: 'QuoteList', query: { leadList: row.item.quotesList } }" target="_blank">
            {{ row.value }}
          </b-link>
        </template>

        <template #custom-foot="data">
          <b-tr>
            <b-th v-for="field in data.fields" :key="field.key">{{ field.key == 'userId' ? 'Totals' : totals[field.key] }}</b-th>
          </b-tr>
        </template>

        <template #cell(show_details)="row">
          <b-btn size="xs" variant="secondary" @click.prevent="toggleDetails(row)">
            {{ row.detailsShowing ? 'Hide' : 'Zoom' }}
          </b-btn>
        </template>
        <template #row-details="row">
          <b-card>
            <LoadingIndicator v-if="row.item.loading" centered />
            <b-table v-if="row.item.rows.length" striped hover :items="row.item.rows" :fields="detailsFields" :tbody-tr-class="rowClass" class="mb-3">
              <template #cell(id)="data">
                <b-link :to="orderLink(data.item)" target="_blank">{{ data.value }}</b-link>
              </template>
              <template #cell(balanceMethodId)="data">
                {{ paymentTypeLabel(data.value) }}
              </template>
            </b-table>

            <b-table v-if="row.item.sourceRows.length" striped hover :items="row.item.sourceRows" :fields="sourceDetailsFields" primary-key="sourceId" class="mb-0"></b-table>
          </b-card>
        </template>
      </b-table>
    </b-card>

    <b-card class="mb-4" v-if="!loading && ndpItems.length">
      <h6 class="mb-3">NDP Sales <small class="text-muted">(dealer portal, by dealer)</small></h6>
      <b-table striped hover :items="ndpItems" :fields="ndpFields" caption-top>
        <template #cell(customerId)="data">
          {{ data.item.customer ? data.item.customer.contact.company || (data.item.customer.contact.firstName + ' ' + data.item.customer.contact.lastName) : '' }}
        </template>
        <template #custom-foot="data">
          <b-tr>
            <b-th v-for="field in data.fields" :key="field.key">{{ field.key == 'customerId' ? 'Totals' : ndpTotals[field.key] }}</b-th>
          </b-tr>
        </template>
      </b-table>
    </b-card>

    <b-card class="mb-4" v-if="!loading && (items.length || ndpItems.length)">
      <h6 class="mb-3">Grand Total <small class="text-muted">(Retail + NDP)</small></h6>
      <b-table-simple striped hover>
        <b-thead>
          <b-tr>
            <b-th>Stream</b-th>
            <b-th>Orders</b-th>
            <b-th>Dispatched</b-th>
            <b-th>Tariffs</b-th>
            <b-th>Carrier Pay</b-th>
            <b-th>Received Pay</b-th>
            <b-th>Gross Profit</b-th>
            <b-th>Avg Broker Fee</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr>
            <b-th>Retail</b-th>
            <b-td>{{ totals.ordersCount || 0 }}</b-td>
            <b-td>{{ totals.dispatchedCount || 0 }}</b-td>
            <b-td>{{ totals.tarrifsTotal || 0 }}</b-td>
            <b-td>{{ totals.carrierPayTotal || 0 }}</b-td>
            <b-td>{{ totals.receivedPay || 0 }}</b-td>
            <b-td>{{ totals.grossProfit || 0 }}</b-td>
            <b-td>{{ totals.averageProfit || 0 }}</b-td>
          </b-tr>
          <b-tr>
            <b-th>NDP</b-th>
            <b-td>{{ ndpTotals.ordersCount || 0 }}</b-td>
            <b-td>{{ ndpTotals.dispatchedCount || 0 }}</b-td>
            <b-td>{{ ndpTotals.tarrifsTotal || 0 }}</b-td>
            <b-td>{{ ndpTotals.carrierPayTotal || 0 }}</b-td>
            <b-td>{{ ndpTotals.receivedPay || 0 }}</b-td>
            <b-td>{{ ndpTotals.grossProfit || 0 }}</b-td>
            <b-td>{{ ndpTotals.averageProfit || 0 }}</b-td>
          </b-tr>
          <b-tr class="font-weight-bold">
            <b-th>Grand Total</b-th>
            <b-td>{{ grandTotal('ordersCount') }}</b-td>
            <b-td>{{ grandTotal('dispatchedCount') }}</b-td>
            <b-td>{{ grandTotal('tarrifsTotal') }}</b-td>
            <b-td>{{ grandTotal('carrierPayTotal') }}</b-td>
            <b-td>{{ grandTotal('receivedPay') }}</b-td>
            <b-td>{{ grandTotal('grossProfit') }}</b-td>
            <b-td>{{ combinedAverageBrokerFee }}</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </b-card>
  </div>
</template>

<script>
import { tableFields } from '@/mixins/methods';
import { ApiClient } from '@/api/ApiClient';
import LoadingIndicator from '@/components/shared/LoadingIndicator';
import permissionsMixin from '/src/mixins/permissions';
import ReportPeriodFilter from '@/components/pages/reports/ReportPeriodFilter';
import moment from 'moment';
import balanceMethodsMixin from '@/mixins/balanceMethods';

export default {
  name: 'ReportSales',
  data() {
    return {
      loading: false,
      empty: false,
      excelExport: false,
      items: [],
      totals: {},
      ndpItems: [],
      ndpTotals: {},
      periodType: 'list',
      filterType: 1,
      timePeriod: 1,
      selectedDates: null,
      form: {
        createdStart: null,
        createdEnd: null,
        dispatchedStart: null,
        dispatchedEnd: null,
      },
      fltConfig: {
        mode: 'range',
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'm/d/Y',
        allowInput: true,
      },
      timeOptions: [
        { text: 'Current month', value: 1 },
        { text: 'Last month', value: 2 },
        { text: 'Current quarter', value: 3 },
        { text: 'Last quarter', value: 4 },
        { text: 'Current year', value: 5 },
        { text: 'All time', value: 6 },
      ],
      dataOptions: [
        { text: 'Created orders - during time frame', value: 1 },
        { text: 'Dispatched orders - during time frame', value: 2 },
      ],
      ndpFields: [
        { key: 'customerId', label: 'Dealer', sortable: false },
        { key: 'leadsCount', label: 'Leads', sortable: true },
        { key: 'quotesCount', label: 'Quotes', sortable: true },
        { key: 'ordersCount', label: 'Orders', sortable: true },
        { key: 'canceledCount', label: 'Canceled', sortable: true },
        { key: 'issuesCount', label: 'Issues', sortable: true },
        { key: 'dispatchedCount', label: 'Dispatched', sortable: true },
        { key: 'tarrifsTotal', label: 'Tariffs', sortable: true },
        { key: 'carrierPayTotal', label: 'Carrier Pay', sortable: true },
        { key: 'receivedPay', label: 'Received Pay', sortable: true },
        { key: 'grossProfit', label: 'Gross Profit', sortable: true },
        { key: 'averageProfit', label: 'Avg Broker Fee', sortable: true },
        { key: 'generatesRevenue', label: 'Generates Revenue', sortable: true },
      ],
      fields: [
        {
          key: 'userId',
          label: 'User',
          sortable: false,
        },
        {
          key: 'leadsCount',
          label: 'Leads',
          sortable: true,
        },
        {
          key: 'quotesCount',
          label: 'Quotes',
          sortable: true,
        },
        {
          key: 'ordersCount',
          label: 'Orders',
          sortable: true,
        },
        {
          key: 'canceledCount',
          label: 'Canceled',
          sortable: true,
        },
        {
          key: 'canceledRate',
          label: 'Canceled %',
          sortable: true,
        },
        {
          key: 'issuesCount',
          label: 'Issues',
          sortable: true,
        },
        {
          key: 'dispatchedCount',
          label: 'Dispatched',
          sortable: true,
        },
        {
          key: 'fullCcCount',
          label: 'Full CC',
          sortable: true,
        },
        {
          key: 'fullCodCount',
          label: 'Full COD',
          sortable: true,
        },
        {
          key: 'tarrifsTotal',
          label: 'Tariffs',
          sortable: true,
        },
        {
          key: 'carrierPayTotal',
          label: 'Carrier Pay',
          sortable: true,
        },
        {
          key: 'receivedPay',
          label: 'Received Pay',
          sortable: true,
        },
        {
          key: 'pendingDepositTotal',
          label: 'Unpaid deposit',
          sortable: true,
        },
        {
          key: 'grossProfit',
          label: 'Gross Profit',
          sortable: true,
        },
        {
          key: 'averageProfit',
          label: 'Avg Broker Fee',
          sortable: true,
        },
        {
          key: 'show_details',
          label: '',
          sortable: false,
        },
      ],
      detailsFields: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
        },
        {
          key: 'source',
          label: 'Source',
          sortable: true,
        },
        {
          key: 'type',
          label: 'Type',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'tarrifs',
          label: 'Tarrif',
          sortable: true,
        },
        {
          key: 'deposit',
          label: 'Deposit',
          sortable: true,
        },
        {
          key: 'carrierPay',
          label: 'Carrier pay',
          sortable: true,
        },
        {
          key: 'brokerFee',
          label: 'Broker Fee',
          sortable: true,
        },
        {
          key: 'customerPaidCompany',
          label: 'Deposit paid',
          sortable: true,
        },
        {
          key: 'pendingDeposit',
          label: 'Unpaid deposit',
          sortable: true,
        },
        {
          key: 'balanceMethodId',
          label: 'Balance method',
          sortable: true,
        },
        {
          key: 'signedAt',
          label: 'Dispatched At',
          sortable: true,
        },
      ],
      sourceDetailsFields: [
        {
          key: 'name',
          label: 'Source',
          sortable: true,
        },
        {
          key: 'deposit',
          label: 'Average Deposit',
          sortable: true,
        },
        {
          key: 'brokerFee',
          label: 'Average Broker Fee',
          sortable: true,
        },
      ],
    };
  },
  mixins: [tableFields, permissionsMixin, balanceMethodsMixin],
  components: {
    LoadingIndicator,
    ReportPeriodFilter,
  },
  computed: {
    combinedAverageBrokerFee() {
      const dispatched = (Number(this.totals.dispatchedCount) || 0) + (Number(this.ndpTotals.dispatchedCount) || 0);
      return dispatched ? Math.round(this.grandTotal('grossProfit') / dispatched) : 0;
    },
  },
  methods: {
    async submit(e) {
      e.preventDefault();
      this.fetchSalesReport();
    },
    toggleDetails(row) {
      row.toggleDetails();
      if (row.item._showDetails) {
        this.fetchSalesReportDetails(row.item);
        this.fetchSalesReportSourceDetails(row.item);
      }
    },
    async fetchSalesReport() {
      this.prepareDates();

      if (this.excelExport) {
        this.form.export = 1;
        const response = await ApiClient.reports.sales({ params: this.form, blobRespone: true });
        delete this.form.export;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'sales.xlsx');
        link.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
        return;
      }

      this.loading = true;
      const [response, ndpResult] = await Promise.all([
        ApiClient.reports.sales({ params: this.form }),
        ApiClient.reports.dealerSales({ params: this.form }).catch(() => null),
      ]);
      this.loading = false;
      this.items = response.data.data.rows.map(x => {
        x.rows = [];
        x.sourceRows = [];
        x.loading = false;
        x.loading2 = false;
        return x;
      });
      this.totals = response.data.data.totals;
      this.ndpItems = ndpResult ? ndpResult.data.data.rows : [];
      this.ndpTotals = ndpResult ? ndpResult.data.data.totals : {};
      this.empty = !this.items.length && !this.ndpItems.length;
    },
    grandTotal(key) {
      return (Number(this.totals[key]) || 0) + (Number(this.ndpTotals[key]) || 0);
    },
    async fetchSalesReportDetails(item) {
      this.prepareDates();
      item.loading = true;
      let params = Object.assign({ userId: item.userId }, this.form);
      const response = await ApiClient.reports.salesDetails({ params });
      item.loading = false;
      item.rows = response.data.data.rows;
    },
    async fetchSalesReportSourceDetails(item) {
      this.prepareDates();
      item.loading2 = true;
      let params = Object.assign({ userId: item.userId }, this.form);
      const response = await ApiClient.reports.salesSourceDetails({ params });
      item.loading2 = false;
      item.sourceRows = response.data.data.rows;
    },
    userLink(id) {
      return id ? { name: 'UserDetails', params: { id: id } } : {};
    },
    orderLink(item) {
      if (item.leadTypeId == 3) return { name: 'OrderDetails', params: { id: item.id } };
      if (item.leadTypeId == 2) return { name: 'QuoteDetails', params: { id: item.id } };
      if (item.leadTypeId == 1) return { name: 'LeadDetails', params: { id: item.id } };
      return null;
    },
    prepareDates() {
      let parts = [];
      if (this.periodType == 'list') {
        if (this.timePeriod == 1) {
          parts.push(moment().startOf('month').format('YYYY-MM-DD'));
          parts.push(moment().endOf('month').format('YYYY-MM-DD'));
        }
        if (this.timePeriod == 2) {
          parts.push(moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'));
          parts.push(moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'));
        }
        if (this.timePeriod == 3) {
          parts.push(moment().startOf('quarter').format('YYYY-MM-DD'));
          parts.push(moment().endOf('quarter').format('YYYY-MM-DD'));
        }
        if (this.timePeriod == 4) {
          parts.push(moment().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD'));
          parts.push(moment().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD'));
        }
        if (this.timePeriod == 5) {
          parts.push(moment().startOf('year').format('YYYY-MM-DD'));
          parts.push(moment().endOf('year').format('YYYY-MM-DD'));
        }
        if (this.timePeriod == 6) {
          parts.push(null);
          parts.push(null);
        }
      } else {
        if (!this.selectedDates) return;
        parts = this.selectedDates.split(' to ');
        if (parts.length < 2) parts.push(parts[0]);
      }

      this.form.createdStart = null;
      this.form.createdEnd = null;
      this.form.dispatchedStart = null;
      this.form.dispatchedEnd = null;

      if (this.filterType == 1) {
        this.form.createdStart = parts[0];
        this.form.createdEnd = parts[1];
      } else {
        this.form.dispatchedStart = parts[0];
        this.form.dispatchedEnd = parts[1];
      }
    },
    rowClass(item, type) {
      if (type === 'row' && item && item.tasksExists) return 'text-danger';
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-danger a {
  color: inherit;
}
</style>
